<template>
  <BaseCard>
    <template #header>
      <template v-if="headerLabel">{{ headerLabel }}</template>
      <template v-else>
        Chart for <strong class="display-username">{{ username }}</strong>
        <span v-if="chartDatetime" class="ml-2 text-sm font-normal text-text-muted">
          {{ chartDatetime.local }} ({{ chartDatetime.timezone }}) · {{ chartDatetime.utc }}
        </span>
      </template>
    </template>

    <p v-if="persistenceWarning" class="mb-3 text-sm text-warning-text">
      {{ persistenceWarning }}
    </p>

    <div v-if="displayAngles.length" class="mb-4">
      <div class="mb-1 font-semibold text-text-secondary">Angles</div>
      <div class="card-scroll-content max-h-48">
        <div
          v-for="angle in displayAngles"
          :key="angle.id"
          class="flex items-baseline gap-x-1.5 text-sm"
        >
          <span
            class="astro-angle"
            :style="{ '--angle-name-weight': settings.angleNameWeight }"
            :title="angle.name"
          >
            {{ formatAngleLabel(angle, compact ? compactSettings : settings) }}
          </span>
          <AstroLabel category="sign" v-bind="formatSignLabelParts(angle, settings)" :settings="settings" />
          <span>{{ formatPositionDegreeText(angle, settings) }}</span>
          <span class="text-text-muted">({{ formatNumericValue(angle.longitude, settings) }}°)</span>
        </div>
      </div>
    </div>

    <div v-if="displayPlanets.length">
      <div class="mb-1 font-semibold text-text-secondary">Planets</div>
      <div class="card-scroll-content max-h-64">
        <div
          v-for="planet in displayPlanets"
          :key="planet.id"
          class="flex items-baseline gap-x-1.5 text-sm"
        >
          <AstroLabel
            category="planet"
            v-bind="formatPlanetLabelParts(planet, compact ? compactSettings : settings)"
            :settings="compact ? compactSettings : settings"
            :title="planet.name"
          />
          <AstroLabel category="sign" v-bind="formatSignLabelParts(planet, settings)" :settings="settings" />
          <span>{{ formatPositionDegreeText(planet, settings) }}</span>
          <span v-if="planet.retrograde" class="text-red-500">℞</span>
          <span v-if="!compact" class="text-text-muted">
            (lon {{ formatNumericValue(planet.longitude, settings) }}°
            lat {{ formatNumericValue(planet.latitude, settings) }}°)
          </span>
        </div>
      </div>
      <p v-if="compact && hiddenPlanetCount > 0" class="mt-2 text-sm text-text-muted">
        And {{ hiddenPlanetCount }} more in Personal Sky.
      </p>
    </div>

    <div v-if="loadError" class="text-sm text-danger-text">{{ loadError }}</div>

    <EmptyState
      v-if="showEmptyCta && !natalPlanets.length && !loadError"
      message="No chart data yet."
    >
      <RouterLink to="/create-chart" class="underline text-accent-text">
        Enter birth data
      </RouterLink>
      to calculate a chart.
    </EmptyState>
  </BaseCard>
</template>

<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import AstroLabel from '@/components/chart/AstroLabel.vue';
import BaseCard from '@/components/common/BaseCard.vue';
import EmptyState from '@/components/common/EmptyState.vue';
import { useChartDisplaySettings } from '@/composables/useChartDisplaySettings';
import {
  formatAngleLabel,
  formatNumericValue,
  formatPlanetLabelParts,
  formatPositionDegreeText,
  formatSignLabelParts,
} from '@/utils/chartFormatters';

const props = defineProps({
  username: {
    type: String,
    default: '',
  },
  chartDatetime: {
    type: Object,
    default: null,
  },
  natalAngles: {
    type: Array,
    default: () => [],
  },
  natalPlanets: {
    type: Array,
    default: () => [],
  },
  persistenceWarning: {
    type: String,
    default: '',
  },
  loadError: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
  headerLabel: {
    type: String,
    default: '',
  },
  showEmptyCta: {
    type: Boolean,
    default: true,
  },
});

const { settings } = useChartDisplaySettings();

const compactSettings = computed(() => ({
  ...settings.value,
  planetNameMode: 'none',
}));

const previewLimit = 6;

const displayAngles = computed(() => {
  if (!props.compact) {
    return props.natalAngles;
  }
  return props.natalAngles.slice(0, 4);
});

const displayPlanets = computed(() => {
  if (!props.compact) {
    return props.natalPlanets;
  }
  return props.natalPlanets.slice(0, previewLimit);
});

const hiddenPlanetCount = computed(() => {
  if (!props.compact) {
    return 0;
  }
  return Math.max(props.natalPlanets.length - previewLimit, 0);
});
</script>
